import types from "../types";
import { getUniqueID } from "../utils/numbers";
import {
  CLIP_PLACEMENT_OVERLAY,
  getClipPlaybackRate,
  VIDEO_EDITOR_DEFAULT_COMPOSITION,
  VIDEO_EDITOR_MIN_CLIP_DURATION,
  VIDEO_EDITOR_TRACK_TYPE,
} from "../features/videoEditor/constants";
import {
  createEmptyEdl,
  getClipTimelineDuration,
  getEdlDuration,
} from "../features/videoEditor/projectSchema";
import {
  CLIP_LAYOUT_MODE,
  clampClipVolume,
  clipHasCanvasLayout,
  clipHasVolume,
  createClipLayoutForType,
  DEFAULT_CLIP_VOLUME,
} from "../features/videoEditor/clipProperties";
import { normalizeVideoEditorEdl } from "../features/videoEditor/persistEdl";
import {
  createEmptyClipSelection,
  normalizeClipSelection,
  pruneClipSelection,
  resolveClipSelection,
  VIDEO_EDITOR_SELECTION_MODE,
} from "../features/videoEditor/clipSelection";
import {
  assignClipToLane,
  reflowAllOverlayLanes,
  removeClipFromTrack,
} from "../features/videoEditor/clipTimelineUtils";

const initialState = {
  edl: null,
  isDirty: false,
  isExporting: false,
  ...createEmptyClipSelection(),
};

const availableClipIds = (edl) => Object.keys(edl?.clips?.byId || {});

const withSelection = (state, selection) => ({
  ...state,
  ...normalizeClipSelection({
    clipIds: selection?.selectedClipIds,
    primaryClipId: selection?.selectedClipId,
    availableIds: availableClipIds(state.edl),
  }),
});

const findTrackForType = ({ edl, type, excludeTrackIds = [] }) => {
  const order = edl.tracks?.order || [];
  return order
    .map((trackId) => edl.tracks.byId[trackId])
    .find(
      (track) =>
        track?.type === type && !excludeTrackIds.includes(track?.id)
    );
};

const compactEmptyOverlayTracks = ({ edl }) =>
  reflowAllOverlayLanes({ edl });

const splitClipInEdl = ({ edl, clipId, splitTime }) => {
  const clip = edl.clips.byId[clipId];
  if (!clip) return edl;

  const clipStart = clip.position;
  const clipEnd = clipStart + getClipTimelineDuration({ clip });
  if (splitTime <= clipStart + VIDEO_EDITOR_MIN_CLIP_DURATION) return edl;
  if (splitTime >= clipEnd - VIDEO_EDITOR_MIN_CLIP_DURATION) return edl;

  const firstDuration = splitTime - clipStart;
  const playbackRate = getClipPlaybackRate({ clip });
  const sourceDelta = firstDuration * playbackRate;
  const secondClipId = getUniqueID();

  const firstClip = {
    ...clip,
    outPoint: clip.inPoint + sourceDelta,
    playbackRate,
  };

  const secondClip = {
    ...clip,
    id: secondClipId,
    inPoint: clip.inPoint + sourceDelta,
    position: splitTime,
    playbackRate,
  };

  if (clip.layout) {
    secondClip.layout = { ...clip.layout };
  }

  const track = edl.tracks.byId[clip.trackId];
  const clipIndex = track?.clipIds?.indexOf(clipId) ?? -1;
  const nextClipIds = [...(track?.clipIds || [])];
  if (clipIndex >= 0) {
    nextClipIds.splice(clipIndex + 1, 0, secondClipId);
  } else {
    nextClipIds.push(secondClipId);
  }

  return {
    ...edl,
    tracks: {
      ...edl.tracks,
      byId: {
        ...edl.tracks.byId,
        [clip.trackId]: {
          ...track,
          clipIds: nextClipIds,
        },
      },
    },
    clips: {
      byId: {
        ...edl.clips.byId,
        [clipId]: firstClip,
        [secondClipId]: secondClip,
      },
    },
  };
};

const applyClipUpdates = ({ edl, clipId, updates }) => {
  const existing = edl.clips.byId[clipId];
  if (!existing) {
    return edl;
  }

  const nextUpdates = { ...updates };
  if (clipHasVolume(existing) && Object.prototype.hasOwnProperty.call(nextUpdates, "volume")) {
    nextUpdates.volume = clampClipVolume(nextUpdates.volume);
  }

  const nextEdl = {
    ...edl,
    clips: {
      byId: {
        ...edl.clips.byId,
        [clipId]: {
          ...existing,
          ...nextUpdates,
        },
      },
    },
  };

  return {
    ...nextEdl,
    duration: getEdlDuration({ edl: nextEdl }),
  };
};

const removeClipIdsFromEdl = ({ edl, clipIds }) => {
  let nextEdl = edl;
  clipIds.forEach((clipId) => {
    const clip = nextEdl.clips.byId[clipId];
    if (!clip) {
      return;
    }
    nextEdl = removeClipFromTrack({
      edl: nextEdl,
      clipId,
    });
    const nextClipsById = { ...nextEdl.clips.byId };
    delete nextClipsById[clipId];
    nextEdl = {
      ...nextEdl,
      clips: {
        byId: nextClipsById,
      },
    };
  });

  nextEdl = compactEmptyOverlayTracks({ edl: nextEdl });
  return {
    ...nextEdl,
    duration: getEdlDuration({ edl: nextEdl }),
  };
};

export const videoEditorReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case types.VIDEO_EDITOR_INIT_PROJECT: {
      return {
        ...state,
        edl: normalizeVideoEditorEdl({
          edl: payload?.edl || createEmptyEdl(),
        }),
        isDirty: false,
        isExporting: false,
        ...createEmptyClipSelection(),
      };
    }
    case types.VIDEO_EDITOR_RESET:
      return initialState;
    case types.VIDEO_EDITOR_SET_PLAYHEAD: {
      if (!state.edl) return state;
      const duration = getEdlDuration({ edl: state.edl });
      const nextPlayhead = Math.min(
        duration,
        Math.max(0, Number(payload?.playhead) || 0)
      );
      if (nextPlayhead === state.edl.playhead) {
        return state;
      }
      return {
        ...state,
        edl: {
          ...state.edl,
          playhead: nextPlayhead,
        },
      };
    }
    case types.VIDEO_EDITOR_COMMIT_CLIP_UPDATE: {
      if (!state.edl || !payload?.clipId) return state;
      return {
        ...state,
        edl: applyClipUpdates({
          edl: state.edl,
          clipId: payload.clipId,
          updates: payload.updates,
        }),
        isDirty: true,
      };
    }
    case types.VIDEO_EDITOR_COMMIT_CLIP_UPDATES: {
      if (!state.edl || !Array.isArray(payload?.updates)) return state;
      let nextEdl = state.edl;
      payload.updates.forEach((entry) => {
        if (!entry?.clipId) {
          return;
        }
        nextEdl = applyClipUpdates({
          edl: nextEdl,
          clipId: entry.clipId,
          updates: entry.updates,
        });
      });
      return {
        ...state,
        edl: nextEdl,
        isDirty: true,
      };
    }
    case types.VIDEO_EDITOR_SPLIT_CLIP_AT_PLAYHEAD: {
      if (!state.edl || !payload?.clipId) return state;
      let nextEdl = splitClipInEdl({
        edl: state.edl,
        clipId: payload.clipId,
        splitTime: state.edl.playhead,
      });
      nextEdl = reflowAllOverlayLanes({ edl: nextEdl });
      return {
        ...state,
        edl: {
          ...nextEdl,
          duration: getEdlDuration({ edl: nextEdl }),
        },
        isDirty: true,
      };
    }
    case types.VIDEO_EDITOR_SET_SELECTED_CLIP: {
      return withSelection(
        state,
        resolveClipSelection({
          currentIds: state.selectedClipIds,
          clipId: payload?.clipId || null,
          mode: VIDEO_EDITOR_SELECTION_MODE.REPLACE,
        })
      );
    }
    case types.VIDEO_EDITOR_SET_SELECTION: {
      return withSelection(
        state,
        resolveClipSelection({
          currentIds: state.selectedClipIds,
          clipId: payload?.clipId,
          clipIds: payload?.clipIds,
          event: payload?.event,
          mode: payload?.mode || VIDEO_EDITOR_SELECTION_MODE.REPLACE,
          preserveMulti: Boolean(payload?.preserveMulti),
        })
      );
    }
    case types.VIDEO_EDITOR_REMOVE_CLIP: {
      if (!state.edl || !payload?.clipId) return state;
      const clip = state.edl.clips.byId[payload.clipId];
      if (!clip) return state;

      const nextEdl = removeClipIdsFromEdl({
        edl: state.edl,
        clipIds: [payload.clipId],
      });

      return {
        ...state,
        edl: nextEdl,
        isDirty: true,
        ...pruneClipSelection({
          selection: state,
          availableIds: availableClipIds(nextEdl),
        }),
      };
    }
    case types.VIDEO_EDITOR_REMOVE_CLIPS: {
      const clipIds = (payload?.clipIds || []).filter(Boolean);
      if (!state.edl || !clipIds.length) return state;

      const nextEdl = removeClipIdsFromEdl({
        edl: state.edl,
        clipIds,
      });

      return {
        ...state,
        edl: nextEdl,
        isDirty: true,
        ...pruneClipSelection({
          selection: state,
          availableIds: availableClipIds(nextEdl),
        }),
      };
    }
    case types.VIDEO_EDITOR_ADD_CLIP: {
      if (!state.edl || !payload?.clip) return state;

      const clip = payload.clip;
      let nextEdl = {
        ...state.edl,
        clips: {
          byId: {
            ...state.edl.clips.byId,
            [clip.id]: {
              ...clip,
              trackId: clip.trackId || null,
            },
          },
        },
      };

      if (
        clip.type === VIDEO_EDITOR_TRACK_TYPE.IMAGE ||
        clip.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO ||
        clip.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO
      ) {
        nextEdl = assignClipToLane({
          edl: nextEdl,
          clipId: clip.id,
          position: clip.position,
        });
      } else {
        let trackId = clip.trackId;
        let tracksById = { ...state.edl.tracks.byId };
        let trackOrder = [...(state.edl.tracks.order || [])];

        if (!trackId) {
          const existingTrack = findTrackForType({
            edl: state.edl,
            type: clip.type,
          });
          if (existingTrack) {
            trackId = existingTrack.id;
          } else {
            trackId = getUniqueID();
            tracksById[trackId] = {
              id: trackId,
              type: clip.type,
              clipIds: [],
            };
            trackOrder = [...trackOrder, trackId];
          }
        }

        const track = tracksById[trackId] || {
          id: trackId,
          type: clip.type,
          clipIds: [],
        };

        nextEdl = {
          ...nextEdl,
          tracks: {
            byId: {
              ...tracksById,
              [trackId]: {
                ...track,
                clipIds: [...(track.clipIds || []), clip.id],
              },
            },
            order: trackOrder.includes(trackId)
              ? trackOrder
              : [...trackOrder, trackId],
          },
          clips: {
            byId: {
              ...nextEdl.clips.byId,
              [clip.id]: {
                ...nextEdl.clips.byId[clip.id],
                trackId,
              },
            },
          },
        };
      }

      return withSelection(
        {
          ...state,
          edl: {
            ...nextEdl,
            duration: getEdlDuration({ edl: nextEdl }),
          },
          isDirty: true,
        },
        {
          selectedClipIds: [clip.id],
          selectedClipId: clip.id,
        }
      );
    }
    case types.VIDEO_EDITOR_APPLY_EDL: {
      if (!payload?.edl) return state;

      const nextEdl = {
        ...payload.edl,
        duration: getEdlDuration({ edl: payload.edl }),
      };

      return {
        ...state,
        edl: nextEdl,
        isDirty: true,
        ...pruneClipSelection({
          selection: state,
          availableIds: availableClipIds(nextEdl),
        }),
      };
    }
    case types.VIDEO_EDITOR_SET_EXPORTING:
      return {
        ...state,
        isExporting: Boolean(payload?.isExporting),
      };
    case types.VIDEO_EDITOR_MARK_SAVED:
      return {
        ...state,
        isDirty: false,
      };
    case types.GLOBAL_RESET_STATE:
    case types.GLOBAL_RESET_ADVERTISER_DATA:
      return initialState;
    default:
      return state;
  }
};

export const createClipForTrack = ({
  type,
  sourceUrl,
  label = "",
  duration = 5,
  position = 0,
  inPoint = 0,
  sourceWidth,
  sourceHeight,
  sourceDuration,
  compositionWidth,
  compositionHeight,
  layoutMode = CLIP_LAYOUT_MODE.OVERLAY,
  volume = DEFAULT_CLIP_VOLUME,
  hasAudio,
}) => {
  const safeDuration = Math.max(duration, VIDEO_EDITOR_MIN_CLIP_DURATION);
  const clip = {
    id: getUniqueID(),
    trackId: null,
    type,
    sourceUrl,
    label,
    inPoint,
    outPoint: inPoint + safeDuration,
    position,
  };

  if (clipHasCanvasLayout({ type })) {
    if (Number(sourceWidth) > 0 && Number(sourceHeight) > 0) {
      clip.sourceWidth = Number(sourceWidth);
      clip.sourceHeight = Number(sourceHeight);
    }
    clip.placement = CLIP_PLACEMENT_OVERLAY;
    clip.layout = createClipLayoutForType({
      type,
      layoutMode,
      sourceWidth,
      sourceHeight,
      compositionWidth:
        compositionWidth || VIDEO_EDITOR_DEFAULT_COMPOSITION.width,
      compositionHeight:
        compositionHeight || VIDEO_EDITOR_DEFAULT_COMPOSITION.height,
    });
    clip.layerIndex = 0;
  }

  if (
    type === VIDEO_EDITOR_TRACK_TYPE.VIDEO ||
    type === VIDEO_EDITOR_TRACK_TYPE.AUDIO
  ) {
    clip.volume = clampClipVolume(volume);
    clip.sourceDuration = sourceDuration ?? safeDuration;
  }

  if (type === VIDEO_EDITOR_TRACK_TYPE.VIDEO && typeof hasAudio === "boolean") {
    clip.hasAudio = hasAudio;
  }

  return clip;
};

export const getDefaultTrackTypeForAsset = ({ type }) => {
  if (type === VIDEO_EDITOR_TRACK_TYPE.AUDIO) {
    return VIDEO_EDITOR_TRACK_TYPE.AUDIO;
  }
  if (type === VIDEO_EDITOR_TRACK_TYPE.IMAGE) {
    return VIDEO_EDITOR_TRACK_TYPE.IMAGE;
  }
  if (type === VIDEO_EDITOR_TRACK_TYPE.SUBTITLE) {
    return VIDEO_EDITOR_TRACK_TYPE.SUBTITLE;
  }
  return VIDEO_EDITOR_TRACK_TYPE.VIDEO;
};
